import clsx from "clsx";
import { cloneDeep } from "lodash-es";
import { FC, useEffect, useState } from "react";

import { downloadJsonFile } from "../../../../../utils/file";
import { updateCourse, useCourseSettingActions } from "../../../../../utils/section";

import { AdminCourseListItem } from "./AdminSectionListItem";

import { serverCloneCourse, serverExportCourse } from "api/api-server-actions";
import { HoverableToolbar } from "components/hover-widgets/HoverableToolbar";
import type { SectionType } from "components/server-types";
import { t } from "i18n/i18n";
import { storeApi, useAppDispatch } from "store/index";
import { OrderedSectionType } from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { toastLocalizedOperationCatcher, toastSuccessMessage } from "utils/alerts";
import { ComponentEventProperties } from "utils/amplitude";
import { useIsArchived } from "utils/hooks/use-is-archived";

type AdminCourseListPanelProps = {
  title: string;
  courses: OrderedSectionType[];
  eventPrefix?: string;
  eventProperties?: ComponentEventProperties["eventProperties"];
  canEdit: boolean;
  isDisabled?: boolean;
};

export const AdminCourseListPanel: FC<AdminCourseListPanelProps> = ({
  title,
  courses,
  eventPrefix,
  eventProperties,
  canEdit,
  isDisabled = false,
}) => {
  const [courseList, setCourseList] = useState<OrderedSectionType[]>(courses);
  // const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
  const { handleCourseDelete } = useCourseSettingActions();
  const { sortByActiveTitles } = useIsArchived();
  const dispatch = useAppDispatch();

  useEffect(() => {
    setCourseList(courses.map(cloneDeep).toSorted(sortByActiveTitles));
  }, [courses, sortByActiveTitles]);

  const handleClone = (courseId: string, courseCodeId?: string, termId?: string) => {
    serverCloneCourse(courseId, courseCodeId, termId)
      .then(() => {
        toastSuccessMessage(t("success.toasts.clone_course_started_success"));
      })
      .catch(toastLocalizedOperationCatcher("copy_course_failure"));
  };

  const handleCourseSettings = (courseId: string) => {
    dispatch(viewStateActions.setCourseSettingsForCourseId(courseId));
  };

  const handleExportCourse = (course: SectionType) => async () => {
    const courseJson = await serverExportCourse(course.id);
    downloadJsonFile(courseJson, `${course.title}.json`);
  };

  const handleMoveTo = (course: SectionType) => (courseCodeId: string, termId: string) => {
    updateCourse({
      id: course.id,
      course_id: courseCodeId,
      term_id: termId,
    });
  };

  const handleToggleArchive = (course: SectionType) => () => {
    storeApi.courses.partial_update({
      id: course.id,
      archived_at: course.archived_at ? null : new Date().toISOString(),
    });
  };

  return (
    <div className="mb-6 border-b border-black-tint-90" role="list">
      <HoverableToolbar reference="course">
        <h3 className={clsx("h3 my-2", isDisabled ? "text-black-tint-55" : "text-black-tint-20")}>
          {title}
        </h3>
      </HoverableToolbar>

      {courseList.map((course) => (
        <AdminCourseListItem
          canEdit={canEdit}
          course={course}
          eventPrefix={eventPrefix}
          eventProperties={eventProperties}
          key={course.id}
          optionsMenuActions={{
            onSettings: () => handleCourseSettings(course.id),
            onDelete: () => handleCourseDelete(course),
            onDuplicate: () => handleClone(course.id),
            onCopyTo: handleClone,
            onMoveTo: handleMoveTo(course),
            onExport: handleExportCourse(course),
            onArchive: handleToggleArchive(course),
            onUnarchive: handleToggleArchive(course),
          }}
        />
      ))}

      {!canEdit && courseList.length === 0 && (
        <div className="body-s ml-2 w-full py-2 text-black-tint-40">
          {t("course_list_panel.no_courses")}
        </div>
      )}
    </div>
  );
};
